import { createContext, useContext } from 'react';

/**
 * Page-level actions available to every widget:
 *   open(actionKey, payload)             open a page-level dialog / navigate (quick actions)
 *   navigateToMaintenancesWithFilters(o) deep link into the work order list (legacy behaviour)
 *   invalidate(prefixes)                 refresh cached data after an action
 */
export const DashboardActionsContext = createContext(null);

const noop = () => {};
const FALLBACK = { open: noop, navigateToMaintenancesWithFilters: noop, invalidate: noop };

export function useDashboardActions() {
  return useContext(DashboardActionsContext) || FALLBACK;
}

/**
 * Verbatim behaviour of MainDesktopView.navigateToMaintenancesWithFilters: writes the work order
 * list preferences for this organization and navigates. When quotes have their own list the
 * work orders list must never show them, so the base states depend on that flag.
 */
export function buildMaintenanceListPrefs(currentOrganization, filterOverrides) {
  const baseStates = currentOrganization?.separate_quotes_list_enabled
    ? ['planned', 'in_progress', 'completed', 'picked_up']
    : ['quote', 'planned', 'in_progress', 'completed', 'picked_up'];
  return {
    sortField: 'schedule__start_time',
    sortDirection: 'asc',
    searchQuery: '',
    filtersOpen: false,
    filters: {
      states: baseStates,
      schedulingStatus: 'all',
      customerStatus: 'all',
      invoiceStatus: 'all',
      mechanics: [],
      manufacturers: [],
      dateRangeType: 'all',
      dateFrom: '',
      dateTo: '',
      ...filterOverrides,
    },
  };
}

export function writeMaintenanceListPrefs(currentOrganization, filterOverrides) {
  const prefs = buildMaintenanceListPrefs(currentOrganization, filterOverrides);
  // Must match the org-scoped key used by MainMaintenanceView (per-org list preferences).
  const storageKey = `maintenances_list_preferences_${currentOrganization?.organization_id || 'none'}`;
  localStorage.setItem(storageKey, JSON.stringify(prefs));
}
